import { FormikErrors, FormikProps } from "formik";
import { useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

import { ISurveyBuilderSurvey } from "../../../../../@types/SurveyBuilder";
import { ISurveyDisplayRules } from "../../../../../@types/Surveys";
import { Button } from "../../../../../components/Button";
import { GraphTypes } from "../../../../../constants/surveys";
import { useToast } from "../../../../../hooks/useToast";
import { surveyBuilderService } from "../../../../../services/surveyBuilderService";
import { toPreviewSurvey } from "../../../../../utils/surveyBuilderMapping";
import { IDisplayRulesOptions } from "../../SurveyBuilder";
import { Footer, ReadOnlyFieldset } from "../../SurveyBuilder.styles";
import { DisplaySettingsFormik } from "./DisplayTab";

// Yup's array messages that never reach a user (see DisplayTab's schema).
const INTERNAL_MESSAGES = ["An array is required"];

function formikErrorMessages(errors: FormikErrors<ISurveyDisplayRules>) {
  const messages = new Set<string>();
  const collect = (value: unknown) => {
    if (typeof value === "string") {
      if (!INTERNAL_MESSAGES.includes(value)) messages.add(value);
    } else if (value && typeof value === "object") {
      Object.values(value).forEach(collect);
    }
  };
  collect(errors);
  return Array.from(messages);
}

interface IDisplayTabPanelProps {
  survey: ISurveyBuilderSurvey;
  languageCode: string;
  readOnly: boolean;
  displayRulesOptions: IDisplayRulesOptions;
  isSaving: boolean;
  run(
    action: () => Promise<ISurveyBuilderSurvey>,
    successMessage?: string
  ): Promise<ISurveyBuilderSurvey | undefined>;
}

/** EN-5261: the version's graph settings ("display rules"), saved on their own endpoint. */
export function DisplayTabPanel({
  survey,
  languageCode,
  readOnly,
  displayRulesOptions,
  isSaving,
  run,
}: IDisplayTabPanelProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const formikRef = useRef<FormikProps<ISurveyDisplayRules>>(null);
  const { version } = survey;

  const sections = useMemo(() => toPreviewSurvey(survey).sections, [survey]);
  const displayRules = useMemo<ISurveyDisplayRules>(
    () => version.displayRules ?? { type: GraphTypes.tableView },
    [version.displayRules]
  );

  const handleSave = async () => {
    const formik = formikRef.current;
    if (!formik) return;

    // Touch every field so the inline errors show, then read the result.
    await formik.submitForm();
    const errors = await formik.validateForm();
    const messages = formikErrorMessages(errors);
    if (messages.length > 0) {
      showToast(messages.join("\n"), "error");
      return;
    }

    if (formik.values.type !== GraphTypes.tableView && !version.scoreStrategy) {
      showToast(
        `${t("surveys_builder_errors_scoreStrategyRequired")}${t(
          "surveys_builder_errors_checkContentTab"
        )}`,
        "error"
      );
      return;
    }

    await run(
      () =>
        surveyBuilderService.replaceDisplayRules(
          version.id,
          formik.values,
          version.rowVersion,
          languageCode
        ),
      t("surveys_builder_toasts_saved")
    );
  };

  return (
    <>
      <ReadOnlyFieldset disabled={readOnly}>
        <DisplaySettingsFormik
          ref={formikRef}
          sections={sections}
          displayRules={displayRules}
          displayRulesOptions={displayRulesOptions}
          hasScoreStrategy={!!version.scoreStrategy}
          domains={version.domains}
        />
      </ReadOnlyFieldset>
      {!readOnly && (
        <Footer>
          <Button
            type="button"
            label={t("builder_save")}
            isLoading={isSaving}
            disabled={isSaving}
            onClick={handleSave}
          />
        </Footer>
      )}
    </>
  );
}
